import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { MultiSelect } from '../ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { NotificationCardHeader } from './NotificationCardHeader';
import { NotificationCardError } from './NotificationCardError';
import { NotificationHourSelect } from './NotificationHourSelect';
import { NotificationMessageEditor } from './NotificationMessageEditor';
import { NotificationResetFooter } from './NotificationResetFooter';
import { NotifyRoleToggles } from './NotifyRoleToggles';
import { MilestoneCheckInNotifyRole, MilestoneCheckInSendMode } from '../../services/slackService';

export const MAX_MILESTONE_DAYS = 3650;
export const MAX_MILESTONE_ENTRIES = 5;

/** Selectable recipient roles, ordered from broadest to narrowest scope. */
export const MILESTONE_NOTIFY_ROLE_OPTIONS: MilestoneCheckInNotifyRole[] = [
  MilestoneCheckInNotifyRole.ADMIN,
  MilestoneCheckInNotifyRole.MANAGER,
  MilestoneCheckInNotifyRole.MEMBER,
];

export interface MilestoneCheckInSettingsDraft {
  notificationHour: number;
  milestoneDays: number[];
  notifyRoles: MilestoneCheckInNotifyRole[];
  sendMode: MilestoneCheckInSendMode;
}

/** True when the draft exceeds the milestone count the backend accepts. */
export const hasTooManyMilestoneDays = (draft: MilestoneCheckInSettingsDraft): boolean =>
  draft.milestoneDays.length > MAX_MILESTONE_ENTRIES;

/**
 * True when the check-in would reach nobody: the notification is DM-only, so a
 * draft without recipient roles is a silently dead reminder.
 */
export const hasNoMilestoneRecipients = (draft: MilestoneCheckInSettingsDraft): boolean =>
  draft.notifyRoles.length === 0;

interface StartDateMilestoneCardProps {
  /** Milestone check-in configuration draft (org-wide settings). */
  draft: MilestoneCheckInSettingsDraft;
  /** Editable notification message body. */
  body: string;
  /**
   * The START_DATE_MILESTONE template's `enabled` flag acts as a master switch.
   * When off, the check-in reminder is not sent.
   */
  enabled: boolean;
  allowedParams: string[];
  /** Validation error for the message body. */
  bodyError?: string | null;
  isResetting?: boolean;
  onEnabledChange: (value: boolean) => void;
  onChange: (patch: Partial<MilestoneCheckInSettingsDraft>) => void;
  onBodyChange: (value: string) => void;
  onReset: () => void;
}

/**
 * Single collapsible "Start Date Milestone Check-in" card unifying the
 * START_DATE_MILESTONE template body/enabled controls with the
 * organization-wide check-in settings (hour, milestone days and recipient
 * toggles). Presentational — all state is owned by the containing page.
 */
export const StartDateMilestoneCard: React.FC<StartDateMilestoneCardProps> = ({
  draft,
  body,
  enabled,
  allowedParams,
  bodyError,
  isResetting,
  onEnabledChange,
  onChange,
  onBodyChange,
  onReset,
}) => {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(false);

  const milestoneDaysAsStrings = [...draft.milestoneDays]
    .sort((a, b) => a - b)
    .map((day) => day.toString());

  const handleMilestoneDaysChange = (values: string[]) => {
    const days = Array.from(
      new Set(
        values
          .map((value) => parseInt(value, 10))
          .filter((day) => Number.isInteger(day) && day > 0 && day <= MAX_MILESTONE_DAYS)
      )
    ).sort((a, b) => a - b);
    onChange({ milestoneDays: days });
  };

  const tooManyMilestones = hasTooManyMilestoneDays(draft);
  const noRecipients = hasNoMilestoneRecipients(draft);

  return (
    <Card data-testid="milestone-checkin-card">
      <NotificationCardHeader
        title={t('milestoneCheckInSettings.title')}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((prev) => !prev)}
        enabled={enabled}
        onEnabledChange={onEnabledChange}
        testIdPrefix="milestone-checkin"
      />

      {expanded && (
        <>
          <CardContent className="space-y-6">
            <p className="text-sm text-muted-foreground">
              {t('milestoneCheckInSettings.description')}
            </p>

            <NotificationHourSelect
              value={draft.notificationHour}
              onChange={(notificationHour) => onChange({ notificationHour })}
              id="milestone-checkin-notification-hour"
              testId="milestone-checkin-notification-hour"
              label={t('milestoneCheckInSettings.notificationHourLabel')}
              hint={t('milestoneCheckInSettings.notificationHourHint')}
            />

            <div className="space-y-2">
              <Label>{t('milestoneCheckInSettings.milestoneDaysLabel')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('milestoneCheckInSettings.milestoneDaysHint')}
              </p>
              <MultiSelect
                value={milestoneDaysAsStrings}
                onChange={handleMilestoneDaysChange}
                placeholder={t('milestoneCheckInSettings.milestoneDaysPlaceholder')}
              />
              {tooManyMilestones ? (
                <NotificationCardError
                  message={t('milestoneCheckInSettings.errors.tooManyMilestones', {
                    max: MAX_MILESTONE_ENTRIES,
                  })}
                  testId="milestone-checkin-days-error"
                />
              ) : (
                draft.milestoneDays.length === MAX_MILESTONE_ENTRIES && (
                  <p className="text-xs text-amber-700" data-testid="milestone-checkin-days-at-max">
                    {t('milestoneCheckInSettings.milestoneDaysAtMax', {
                      max: MAX_MILESTONE_ENTRIES,
                    })}
                  </p>
                )
              )}
            </div>

            <div className="space-y-3">
              <div>
                <Label>{t('milestoneCheckInSettings.notifyRolesLabel')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('milestoneCheckInSettings.notifyRolesHint')}
                </p>
              </div>
              <NotifyRoleToggles
                options={MILESTONE_NOTIFY_ROLE_OPTIONS}
                selected={draft.notifyRoles}
                onChange={(notifyRoles) => onChange({ notifyRoles })}
                translationPrefix="milestoneCheckInSettings.notifyRoles"
                testIdPrefix="milestone-checkin"
              />
              {noRecipients && (
                <NotificationCardError
                  message={t('milestoneCheckInSettings.errors.noRecipients')}
                  testId="milestone-checkin-no-recipients-error"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="milestone-checkin-send-mode">
                {t('milestoneCheckInSettings.sendModeLabel')}
              </Label>
              <Select
                value={draft.sendMode}
                onValueChange={(value) => onChange({ sendMode: value as MilestoneCheckInSendMode })}
              >
                <SelectTrigger
                  id="milestone-checkin-send-mode"
                  className="w-56"
                  data-testid="milestone-checkin-send-mode"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(MilestoneCheckInSendMode).map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {t(`milestoneCheckInSettings.sendModes.${mode}.label`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t(`milestoneCheckInSettings.sendModes.${draft.sendMode}.hint`)}
              </p>
            </div>

            <NotificationMessageEditor
              id="milestone-checkin-body"
              testIdPrefix="milestone-checkin"
              body={body}
              onBodyChange={onBodyChange}
              allowedParams={allowedParams}
              error={bodyError}
            />
          </CardContent>
          <NotificationResetFooter
            onReset={onReset}
            isResetting={isResetting}
            testId="milestone-checkin-reset"
          />
        </>
      )}
    </Card>
  );
};
